import { useEffect } from "react";

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  hreflangRu?: string;
  jsonLd?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

export default function PageSEO({
  title,
  description,
  keywords,
  canonical,
  hreflangRu,
  jsonLd,
}: PageSEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set or create a meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        const parts = selector.match(/\[([^\]]+)="([^"]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);

    // Canonical
    let canonicalEl = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${SITE_URL}${canonical}`;

    // hreflang self (en)
    let hreflangEn = document.querySelector<HTMLLinkElement>(`link[rel='alternate'][hreflang='en'][data-page='true']`);
    if (!hreflangEn) {
      hreflangEn = document.createElement("link");
      hreflangEn.rel = "alternate";
      hreflangEn.setAttribute("hreflang", "en");
      hreflangEn.setAttribute("data-page", "true");
      document.head.appendChild(hreflangEn);
    }
    hreflangEn.href = `${SITE_URL}${canonical}`;

    // hreflang ru
    let hreflangRuEl = document.querySelector<HTMLLinkElement>(`link[rel='alternate'][hreflang='ru'][data-page='true']`);
    if (hreflangRu) {
      if (!hreflangRuEl) {
        hreflangRuEl = document.createElement("link");
        hreflangRuEl.rel = "alternate";
        hreflangRuEl.setAttribute("hreflang", "ru");
        hreflangRuEl.setAttribute("data-page", "true");
        document.head.appendChild(hreflangRuEl);
      }
      hreflangRuEl.href = `${SITE_URL}${hreflangRu}`;
    }

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-page="true"]');
    existingScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-page", "true");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Restore defaults on unmount
      document.title = "OEM Auto Parts Sourcing Guangzhou | Engine Block Foundry China | Chirunnexa";
      const pageScripts = document.querySelectorAll('script[type="application/ld+json"][data-page="true"]');
      pageScripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, hreflangRu, jsonLd]);

  return null;
}
